import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useListenerMessage } from '../../../hooks/subscription/use-listener-message'
import { useSub } from '../../../hooks/subscription/use-sub'
import { useGlobalStore } from '../../../store/global-store'
import type {
  LiveGiftsAnimations,
  Positions,
} from '../../../types/lives/animations'
import { Animation } from './components/animation'

const LIVE_GIFTS_ANIMATIONS = '/LIVE-GIFTS-ANIMATIONS'

// assinatura de uma animação (gift + destinos) p/ casar gatilho local x eco pubsub
const sigOf = (a: { imgGift?: string; elementsToGo?: (number | string)[] }) =>
  `${a?.imgGift ?? ''}|${(a?.elementsToGo ?? []).map(String).sort().join(',')}`

export const GiftsAnimations = memo(() => {
  const liveId = useGlobalStore(useShallow((state) => state.liveId))

  const [animation, setAnimation] = useState<LiveGiftsAnimations[]>([])

  // id incremental p/ key única (Date.now() sozinho colide se 2 chegam no mesmo ms)
  const idRef = useRef(0)

  // O backend já manda UMA notificação consolidada por envio (elementsToGo com
  // TODAS as seats). Processa cada mensagem NA HORA — SEM agregação/delay. Antes
  // o BATCH_MS de 400ms atrasava a animação e ainda FUNDIA envios rápidos num só
  // (o timer reiniciava a cada msg) → 2º envio "demorava".
  const checkGiftReceived = useCallback(
    (liveGiftAnimation: LiveGiftsAnimations) => {
      const seen = new Set<string>()
      const els = (liveGiftAnimation.elementsToGo ?? []).filter((e) => {
        const k = String(e)
        // mantém 'streamer' (host) como destino — voa pro topo-centro (HOST) na
        // Animation; só remove duplicados.
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
      if (!els.length) return
      idRef.current += 1
      setAnimation((prev) =>
        prev.concat({
          ...liveGiftAnimation,
          elementsToGo: els as string[],
          id: `${Date.now()}-${idRef.current}`,
        }),
      )
    },
    [],
  )

  const removeAnimation = useCallback((id: string) => {
    setAnimation((prevState) =>
      prevState.filter((animation) => animation.id !== id),
    )
  }, [])

  // meu userId (via URL ?myUserId=) — pra DEDUPAR o eco do pubsub do MEU próprio
  // envio, que já foi animado LOCALMENTE. Fail-safe: só ignora o pubsub se casar
  // com um gatilho local recente; se o local foi perdido (webview recarregando,
  // envio no 1º segundo), o pubsub anima normalmente.
  const myUserId = useMemo(() => {
    const v = Number(new URLSearchParams(window.location.search).get('myUserId'))
    return Number.isFinite(v) && v > 0 ? v : undefined
  }, [])

  // assinaturas de gatilhos locais recentes (p/ casar com o eco do pubsub)
  const recentLocalRef = useRef<{ sig: string; ts: number }[]>([])

  const onPubSub = useCallback(
    (msg: LiveGiftsAnimations) => {
      if (myUserId && Number((msg as { userId?: number })?.userId) === myUserId) {
        const now = Date.now()
        recentLocalRef.current = recentLocalRef.current.filter(
          (r) => now - r.ts < 4000,
        )
        const idx = recentLocalRef.current.findIndex((r) => r.sig === sigOf(msg))
        if (idx >= 0) {
          recentLocalRef.current.splice(idx, 1) // consome → dedupe (já animou local)
          return
        }
        // sem correspondência → local perdido → anima pelo pubsub (fail-safe)
      }
      checkGiftReceived(msg)
    },
    [checkGiftReceived, myUserId],
  )

  useSub<LiveGiftsAnimations>({
    endpoint: `${LIVE_GIFTS_ANIMATIONS}/${liveId}`,
    skip: !liveId,
    onData: onPubSub,
  })

  const [positions, setPositions] = useState<Record<string, Positions> | null>(
    null,
  )
  // espelha se já recebemos alguma âncora (usado p/ parar o retry do request)
  const positionsRef = useRef(false)

  const allPositions = useMemo(
    () => positions && Object.entries(positions),
    [positions],
  )

  useListenerMessage((data) => {
    const message = JSON.parse(data)
    // gatilho LOCAL de animação (instantâneo): o app posta no ATO do toque, sem
    // esperar o roundtrip do pubsub. Anima na hora; não mexe nas posições.
    if (message?.__localAnim) {
      recentLocalRef.current.push({
        sig: sigOf(message.__localAnim),
        ts: Date.now(),
      })
      checkGiftReceived(message.__localAnim)
      return
    }
    if (message && Object.keys(message).length) positionsRef.current = true
    setPositions((prevState) => ({ ...prevState, ...message }))
  })

  // pede as posições das seats ao app RN assim que monta (ou remonta após um
  // reload do webview). Sem isso, após recarregar o webview fica SEM âncoras e o
  // leque não distribui — o presente "para no meio". O RN responde reenviando.
  // Faz RETRY até chegar posição: cobre tanto o reload do webview quanto a corrida
  // em que o webview carrega ANTES das seats medirem (aí o store ainda está vazio).
  useEffect(() => {
    const rn = (
      window as unknown as {
        ReactNativeWebView?: { postMessage: (m: string) => void }
      }
    ).ReactNativeWebView
    if (!rn?.postMessage) return
    let tries = 0
    const ask = () => rn.postMessage('request-positions')
    ask()
    const iv = setInterval(() => {
      tries += 1
      // para de pedir quando já temos âncoras (ou após ~6s de tentativas)
      if (positionsRef.current || tries > 10) {
        clearInterval(iv)
        return
      }
      ask()
    }, 600)
    return () => clearInterval(iv)
  }, [])

  return (
    <div className="w-screen h-screen flex overflow-hidden">
      {animation.map((animation) => (
        <Animation
          key={animation.id}
          animation={animation}
          removeAnimation={removeAnimation}
        />
      ))}

      {allPositions?.map(([key, position]) => (
        <div
          id={key}
          key={key}
          // translate -50% CENTRALIZA a âncora no ponto enviado (= centro do avatar),
          // então getBoundingClientRect().center bate no centro do avatar,
          // independente do tamanho da âncora. O presente pousa DENTRO da imagem.
          className="absolute w-20 h-20 text-white text-xs"
          style={{
            top: position?.y,
            left: position?.x,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </div>
  )
})
